import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRequirementSetListItem,
  RequirementSetCopyRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import DateOnlyPicker from "./DateOnlyPicker";
import { currentBusinessYear, dateOnlyYear, toDateOnly } from "./dateOnlyField";
import ensureOk from "./ensureOk";

type NewSetValues = {
  name?: string;
  effectiveDate?: string;
  endDate?: string;
  sourceRequirementSetId?: string;
};

const { Form } = withTypes<NewSetValues>();

type NewRequirementSetDialogProps = {
  licenseId: string;
  licenseDisplayName?: string;
  sets: IRequirementSetListItem[];
  open: boolean;
  onClose: () => void;
  onCreated: (requirementSetId: string) => void;
};

/**
 * Creating a new set is how a published set is changed: the current rules are copied into a fresh
 * Draft that goes through review again, and the approved one is left exactly as it is.
 */
const NewRequirementSetDialog = ({
  licenseId,
  licenseDisplayName,
  sets,
  open,
  onClose,
  onCreated,
}: NewRequirementSetDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementSetService } = useGlobalContext();

  // Read in UTC: a DateOnly arrives as UTC midnight, and slicing its local string yields NaN. The
  // floor is the current year in the portal's zone, not the browser's.
  const latestYear = sets.reduce(
    (year, set) => Math.max(year, dateOnlyYear(set.effectiveDate) ?? 0),
    currentBusinessYear()
  );

  const newYear = latestYear + 2;

  const handleSubmit = async (values: NewSetValues) => {
    try {
      const response = await requirementSetService.copyRequirementSet(
        RequirementSetCopyRequest.fromJS({
          licenseId,
          name: values.name,
          effectiveDate: toDateOnly(values.effectiveDate),
          endDate: toDateOnly(values.endDate),
          sourceRequirementSetId: values.sourceRequirementSetId || undefined,
        })
      );

      ensureOk(response);
      onCreated(response.id!);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const init = {
      name: `${licenseDisplayName ?? ""} ${newYear}`.trim(),
      effectiveDate: `${newYear}-01-01`,
      endDate: "",
      sourceRequirementSetId: sets[0]?.id ?? "",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle>{t({ id: "newRequirementSet" })}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        required
                        variant="standard"
                        fullWidth
                        label={t({ id: "setName" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="effectiveDate" required>
                  {({ input, meta }) => (
                    <Grid item xs={12} sm={6}>
                      <DateOnlyPicker
                        required
                        label={t({ id: "effectiveDate" })}
                        value={input.value}
                        onChange={input.onChange}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed && meta.error}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="endDate">
                  {({ input }) => (
                    <Grid item xs={12} sm={6}>
                      <DateOnlyPicker
                        label={t({ id: "endDateLabel" })}
                        value={input.value}
                        onChange={input.onChange}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="sourceRequirementSetId">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <TextField
                        select
                        variant="standard"
                        fullWidth
                        label={t({ id: "copyRequirementsFrom" })}
                        {...input}
                      >
                        <MenuItem value="">{t({ id: "startEmpty" })}</MenuItem>
                        {sets.map((set) => (
                          <MenuItem key={set.id} value={set.id}>
                            {`${set.name} · ${set.statusName}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{submitError}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "create" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default NewRequirementSetDialog;
